import { evictDisposableEntries } from "./safeStorage";

export const API_CACHE_TTL = {
  SIX_HOURS: 6 * 60 * 60 * 1000,
  ONE_DAY: 24 * 60 * 60 * 1000,
  RELEASE_PAGE: 30 * 24 * 60 * 60 * 1000,
} as const;

export const API_MISS_TTL = {
  ONE_DAY: 24 * 60 * 60 * 1000,
} as const;

export const LOOKUP_MAX_ATTEMPTS = 3;
export const LOOKUP_FIRST_BACKOFF_MS = 30 * 1000;
export const LOOKUP_MAX_BACKOFF_MS = 10 * 60 * 1000;

export const API_CACHE_MAX_ENTRIES = 200;
export const API_MISS_MAX_ENTRIES = 500;
export const ASSET_INDEX_MAX_URLS = 400;
const ASSET_INDEX_MAX_KEYS_PER_URL = 20;
const RETRIED_ASSETS = new Set<string>();

interface InFlightRequest {
  promise: Promise<unknown>;
  controller: AbortController;
  refs: number;
}

interface LookupFailure {
  attempts: number;
  blockedUntil: number;
}

interface CacheEnvelope<T> {
  value: T;
  createdAt: number;
  expiresAt: number;
}

const IN_FLIGHT_REQUESTS = new Map<string, InFlightRequest>();
const LOOKUP_FAILURES = new Map<string, LookupFailure>();
const RESPONSE_CACHE = new Map<string, CacheEnvelope<unknown>>();
const MISS_CACHE = new Map<string, number>();
const ASSET_INDEX = new Map<string, string[]>();

export interface CachedApiOptions<T> {
  ttlMs: number;
  cacheKey?: string;
  shouldCache?: (data: T) => boolean;
  missTtlMs?: number;
  isMiss?: (data: T) => boolean;
  signal?: AbortSignal;
}

export class ApiLookupMissError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = "ApiLookupMissError";
    this.status = status;
  }
}

export class ApiRequestBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ApiRequestBlockedError";
  }
}

const createAbortError = () => {
  const error = new Error("Request aborted");
  error.name = "AbortError";
  return error;
};

export const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === "AbortError";

export const isTerminalLookupError = (error: unknown): boolean =>
  error instanceof ApiLookupMissError || error instanceof ApiRequestBlockedError;

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = stableValue((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }
  return value;
};

export const stableStringify = (value: unknown): string => JSON.stringify(stableValue(value));

const normalizeKeyParams = (payload: unknown): unknown => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return payload;
  const record = payload as Record<string, unknown>;
  if (typeof record.artist !== "string") return payload;
  return { ...record, artist: record.artist.toLowerCase() };
};

export const createApiCacheKey = (endpoint: string, payload?: unknown): string => {
  return `${endpoint}:${stableStringify(normalizeKeyParams(payload || {}))}`;
};

export const createApiGetUrl = (endpoint: string, params: Record<string, unknown>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      if (value.length > 0) search.set(key, value.join("|"));
      return;
    }
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `${endpoint}?${query}` : endpoint;
};

export const createApiGetCacheKey = (endpoint: string, params: Record<string, unknown>): string =>
  createApiGetUrl(endpoint, normalizeKeyParams(params) as Record<string, unknown>);

export const fetchCachedGetApi = <T>(
  endpoint: string,
  params: Record<string, unknown>,
  options: CachedApiOptions<T>
): Promise<T> => {
  return fetchCachedApi<T>(createApiGetUrl(endpoint, params), { method: "GET" }, {
    ...options,
    cacheKey: options.cacheKey || createApiGetCacheKey(endpoint, params),
  });
};

const parseBody = (body?: BodyInit | null): unknown => {
  if (!body || typeof body !== "string") return undefined;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

const shouldCacheDefault = <T>(data: T): boolean => {
  if (!data || typeof data !== "object") return data !== null && data !== undefined;
  const record = data as Record<string, unknown>;
  if (record.error) return false;
  return true;
};

const trimOldest = <K, V>(map: Map<K, V>, max: number) => {
  while (map.size > max) {
    const oldest = map.keys().next();
    if (oldest.done) return;
    map.delete(oldest.value);
  }
};

export const purgeLegacyApiStorage = (): number =>
  evictDisposableEntries("local") + evictDisposableEntries("session");

export const clearApiCache = () => {
  RESPONSE_CACHE.clear();
  MISS_CACHE.clear();
  ASSET_INDEX.clear();
  RETRIED_ASSETS.clear();
};

export const getApiCacheSize = (): number => RESPONSE_CACHE.size;

export const normalizeAssetUrl = (input?: string): string => {
  if (!input) return "";
  try {
    const url = new URL(input, typeof window !== "undefined" ? window.location.origin : "https://truevined.com");
    const proxiedUrl = url.searchParams.get("url");
    if (proxiedUrl && url.pathname.includes("/.netlify/images")) {
      return normalizeAssetUrl(proxiedUrl);
    }
    url.searchParams.delete("cacheBust");
    return url.toString();
  } catch {
    return input.split("?cacheBust=")[0];
  }
};

const collectAssetUrls = (value: unknown, urls = new Set<string>()): Set<string> => {
  if (typeof value === "string") {
    if (
      value.startsWith("http") &&
      (value.includes("i.scdn.co") ||
        value.includes("i.ytimg.com") ||
        value.includes("yt3.ggpht.com") ||
        value.includes("googleusercontent.com"))
    ) {
      urls.add(normalizeAssetUrl(value));
    }
    return urls;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectAssetUrls(item, urls));
    return urls;
  }

  if (value && typeof value === "object") {
    Object.values(value as Record<string, unknown>).forEach((item) => collectAssetUrls(item, urls));
  }

  return urls;
};

const indexAssetsForCache = (cacheKey: string, data: unknown) => {
  const assetUrls = collectAssetUrls(data);
  if (assetUrls.size === 0) return;

  assetUrls.forEach((assetUrl) => {
    const keys = new Set(ASSET_INDEX.get(assetUrl) || []);
    keys.add(cacheKey);
    ASSET_INDEX.delete(assetUrl);
    ASSET_INDEX.set(assetUrl, Array.from(keys).slice(-ASSET_INDEX_MAX_KEYS_PER_URL));
  });
  trimOldest(ASSET_INDEX, ASSET_INDEX_MAX_URLS);
};

export const getCachedApiValue = <T>(cacheKey: string): T | null => {
  const envelope = RESPONSE_CACHE.get(cacheKey);
  if (!envelope) return null;
  if (envelope.expiresAt <= Date.now()) {
    RESPONSE_CACHE.delete(cacheKey);
    return null;
  }
  RESPONSE_CACHE.delete(cacheKey);
  RESPONSE_CACHE.set(cacheKey, envelope);
  return envelope.value as T;
};

export const setCachedApiValue = <T>(cacheKey: string, value: T, ttlMs: number) => {
  if (!ttlMs || ttlMs <= 0) return;
  const now = Date.now();
  RESPONSE_CACHE.delete(cacheKey);
  RESPONSE_CACHE.set(cacheKey, { value, createdAt: now, expiresAt: now + ttlMs });
  trimOldest(RESPONSE_CACHE, API_CACHE_MAX_ENTRIES);
  indexAssetsForCache(cacheKey, value);
};

export const isCachedApiMiss = (cacheKey: string): boolean => {
  const expiresAt = MISS_CACHE.get(cacheKey);
  if (expiresAt === undefined) return false;
  if (expiresAt <= Date.now()) {
    MISS_CACHE.delete(cacheKey);
    return false;
  }
  return true;
};

export const setCachedApiMiss = (cacheKey: string, ttlMs: number) => {
  if (!ttlMs) return;
  MISS_CACHE.delete(cacheKey);
  MISS_CACHE.set(cacheKey, Date.now() + ttlMs);
  trimOldest(MISS_CACHE, API_MISS_MAX_ENTRIES);
};

export const clearCachedApiMiss = (cacheKey: string) => {
  MISS_CACHE.delete(cacheKey);
};

export const isLookupBlocked = (cacheKey: string): boolean => {
  const failure = LOOKUP_FAILURES.get(cacheKey);
  if (!failure) return false;
  if (failure.attempts >= LOOKUP_MAX_ATTEMPTS) return true;
  return Date.now() < failure.blockedUntil;
};

export const recordLookupFailure = (cacheKey: string) => {
  const previous = LOOKUP_FAILURES.get(cacheKey);
  const attempts = (previous?.attempts || 0) + 1;
  const backoff = Math.min(LOOKUP_FIRST_BACKOFF_MS * 2 ** (attempts - 1), LOOKUP_MAX_BACKOFF_MS);
  LOOKUP_FAILURES.set(cacheKey, { attempts, blockedUntil: Date.now() + backoff });
};

export const clearLookupFailure = (cacheKey: string) => {
  LOOKUP_FAILURES.delete(cacheKey);
};

export const resetLookupGuards = () => {
  LOOKUP_FAILURES.clear();
  IN_FLIGHT_REQUESTS.clear();
  clearApiCache();
};

export const invalidateApiCache = (cacheKey: string) => {
  RESPONSE_CACHE.delete(cacheKey);
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("truevined-api-cache-invalidated", { detail: { cacheKey } }));
};

export const invalidateApiCacheByAsset = (assetUrl: string): string[] => {
  const normalizedUrl = normalizeAssetUrl(assetUrl);
  const cacheKeys = ASSET_INDEX.get(normalizedUrl) || [];
  cacheKeys.forEach(invalidateApiCache);
  ASSET_INDEX.delete(normalizedUrl);
  return cacheKeys;
};

export const registerAssetFailure = (assetUrl: string): boolean => {
  const normalizedUrl = normalizeAssetUrl(assetUrl);
  if (!normalizedUrl || RETRIED_ASSETS.has(normalizedUrl)) return false;
  RETRIED_ASSETS.add(normalizedUrl);
  return invalidateApiCacheByAsset(normalizedUrl).length > 0;
};

export const withCacheBust = (assetUrl: string, seed: number = Date.now()): string => {
  if (!assetUrl) return assetUrl;
  try {
    const url = new URL(assetUrl, typeof window !== "undefined" ? window.location.origin : "https://truevined.com");
    url.searchParams.set("cacheBust", String(seed));
    if (assetUrl.startsWith("/")) {
      return `${url.pathname}${url.search}`;
    }
    return url.toString();
  } catch {
    const separator = assetUrl.includes("?") ? "&" : "?";
    return `${assetUrl}${separator}cacheBust=${seed}`;
  }
};

export async function fetchCachedApi<T>(
  endpoint: string,
  init: RequestInit = {},
  options: CachedApiOptions<T>
): Promise<T> {
  const cacheKey = options.cacheKey || createApiCacheKey(endpoint, parseBody(init.body));
  const cached = getCachedApiValue<T>(cacheKey);
  if (cached) return cached;

  if (options.missTtlMs && isCachedApiMiss(cacheKey)) {
    throw new ApiLookupMissError(`No result for ${endpoint}`, 404);
  }

  if (isLookupBlocked(cacheKey)) {
    throw new ApiRequestBlockedError(`Lookup temporarily blocked for ${endpoint}`);
  }

  const { signal } = options;
  if (signal?.aborted) throw createAbortError();

  let entry = IN_FLIGHT_REQUESTS.get(cacheKey);
  if (entry?.controller.signal.aborted) {
    IN_FLIGHT_REQUESTS.delete(cacheKey);
    entry = undefined;
  }

  if (!entry) {
    const controller = new AbortController();
    const promise = (async () => {
      const response = await fetch(endpoint, { ...init, signal: controller.signal });

      if (!response.ok) {
        if (response.status === 404 || response.status === 410) {
          if (options.missTtlMs) setCachedApiMiss(cacheKey, options.missTtlMs);
          throw new ApiLookupMissError(`No result for ${endpoint}`, response.status);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as T;
      clearLookupFailure(cacheKey);

      if (options.isMiss?.(data)) {
        if (options.missTtlMs) setCachedApiMiss(cacheKey, options.missTtlMs);
        throw new ApiLookupMissError(`No result for ${endpoint}`, response.status);
      }

      if ((options.shouldCache || shouldCacheDefault)(data)) {
        clearCachedApiMiss(cacheKey);
        setCachedApiValue(cacheKey, data, options.ttlMs);
      }

      return data;
    })();

    const created: InFlightRequest = { promise, controller, refs: 0 };
    entry = created;
    IN_FLIGHT_REQUESTS.set(cacheKey, created);

    promise
      .catch((error) => {
        if (!isAbortError(error) && !isTerminalLookupError(error)) recordLookupFailure(cacheKey);
      })
      .finally(() => {
        if (IN_FLIGHT_REQUESTS.get(cacheKey) === created) IN_FLIGHT_REQUESTS.delete(cacheKey);
      });
  }

  const current = entry;
  current.refs += 1;
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    current.refs -= 1;
    if (current.refs <= 0) {
      if (IN_FLIGHT_REQUESTS.get(cacheKey) === current) IN_FLIGHT_REQUESTS.delete(cacheKey);
      current.controller.abort();
    }
  };

  if (!signal) {
    try {
      return (await current.promise) as T;
    } finally {
      release();
    }
  }

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      release();
      reject(createAbortError());
    };

    signal.addEventListener("abort", onAbort, { once: true });

    current.promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        release();
        resolve(value as T);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        release();
        reject(error);
      }
    );
  });
}

export async function fetchOptionalGetApi<T>(
  endpoint: string,
  params: Record<string, unknown>,
  options: CachedApiOptions<T>
): Promise<T | null> {
  try {
    return await fetchCachedGetApi<T>(endpoint, params, {
      missTtlMs: API_MISS_TTL.ONE_DAY,
      ...options,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    return null;
  }
}
